import React, { useState, useEffect, useCallback, useMemo } from "react";
import { toast } from "sonner";
import {
  Crosshair, Plus, Trash2, RefreshCw, Search, Clock, CheckCircle2, XCircle,
  AlertTriangle, Loader2, Trophy,
} from "lucide-react";
import { apiGet, apiPost, apiFetch, safeJson } from "@/lib/api";
import { LoadingBlock } from "@/components/Spinner";

const fmtTime = (iso) => {
  if (!iso) return "";
  try { return new Date(iso.replace("Z", "+00:00")).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
};

const STATUS = {
  waiting: { label: "Waiting", color: "#94a3b8", Icon: Clock },
  bet_placed: { label: "Bet placed (set 1)", color: "#3b82f6", Icon: CheckCircle2 },
  set1_lost_rebet: { label: "Lost set 1 — re-backed", color: "#f59e0b", Icon: RefreshCw },
  won: { label: "Won", color: "#10b981", Icon: Trophy },
  lost: { label: "Lost", color: "#ef4444", Icon: XCircle },
  missed: { label: "Missed window", color: "#6b7280", Icon: AlertTriangle },
};

const StatusBadge = ({ status }) => {
  const s = STATUS[status] || STATUS.waiting;
  const Icon = s.Icon;
  return (
    <span data-testid="sh-status-badge" className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold"
      style={{ backgroundColor: s.color + "22", color: s.color, border: `1px solid ${s.color}55` }}>
      <Icon size={11} /> {s.label}
    </span>
  );
};

export default function SetHandicap() {
  const [selections, setSelections] = useState([]);
  const [prematch, setPrematch] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingPre, setLoadingPre] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [search, setSearch] = useState("");
  const [busyEvent, setBusyEvent] = useState("");

  const loadSelections = useCallback(async () => {
    try {
      const d = await apiGet("/api/set-handicap/selections");
      setSelections(d?.selections || []);
    } catch { /* ignore */ }
    setLoading(false);
  }, []);

  const loadPrematch = useCallback(async () => {
    setLoadingPre(true);
    try {
      const d = await apiGet("/api/set-handicap/prematch");
      setPrematch(d?.matches || []);
    } catch { toast.error("Could not load pre-match list"); }
    setLoadingPre(false);
  }, []);

  useEffect(() => { loadSelections(); }, [loadSelections]);
  useEffect(() => {
    loadSelections();
    const t = setInterval(loadSelections, 20000);
    return () => clearInterval(t);
  }, [loadSelections]);

  const openAdd = () => { setShowAdd(true); loadPrematch(); };

  const addSelection = async (event_id, our_side, player) => {
    setBusyEvent(event_id + our_side);
    try {
      const res = await apiFetch("/api/set-handicap/selections", {
        method: "POST", body: JSON.stringify({ event_id, our_side }),
      });
      const d = await safeJson(res);
      if (res.ok) {
        toast.success(`Added ${player} to Set Handicap`);
        await loadSelections();
      } else {
        toast.error(d?.detail || "Could not add match");
      }
    } catch { toast.error("Could not add match"); }
    setBusyEvent("");
  };

  const removeSelection = async (id) => {
    try {
      const res = await apiFetch(`/api/set-handicap/selections/${id}`, { method: "DELETE" });
      if (res.ok) { toast.success("Removed"); setSelections((s) => s.filter((x) => x.id !== id)); }
      else toast.error("Could not remove");
    } catch { toast.error("Could not remove"); }
  };

  const selectedEventIds = useMemo(() => new Set(selections.map((s) => s.event_id)), [selections]);

  const filteredPre = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return prematch;
    return prematch.filter((m) => (m.name || "").toLowerCase().includes(q));
  }, [prematch, search]);

  const active = selections.filter((s) => ["waiting", "bet_placed", "set1_lost_rebet"].includes(s.status));
  const done = selections.filter((s) => ["won", "lost", "missed"].includes(s.status));

  return (
    <div className="max-w-5xl mx-auto pb-24" data-testid="set-handicap-screen">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Crosshair size={22} className="text-[#8b5cf6]" />
          <div>
            <h1 className="text-lg font-bold text-white">Set Handicap</h1>
            <p className="text-[12px] text-[#94a3b8]">Back your player at +1.5 sets (Bo3) / +2.5 (Bo5). Win 1 set = win the bet.</p>
          </div>
        </div>
        <button data-testid="sh-add-btn" onClick={openAdd}
          className="flex items-center gap-1.5 bg-[#8b5cf6] hover:bg-[#7c3aed] text-white font-bold text-sm px-3.5 py-2 rounded-xl">
          <Plus size={16} /> Add Match
        </button>
      </div>

      <div className="bg-[#1e293b]/60 border border-[#334155] rounded-xl p-3 mb-4 text-[12px] text-[#cbd5e1] leading-relaxed">
        Enable <span className="text-[#a78bfa] font-semibold">Set Handicap Strategy</span> in Settings, then add matches here.
        The engine places the bet pre-match (or until 4 games of set 1 are done). If your player loses set 1,
        it re-backs once at better odds. Works independently of your other strategies.
      </div>

      {loading ? (
        <LoadingBlock />
      ) : (
        <>
          <h2 className="text-base font-bold text-[#e2e8f0] mb-2">Active ({active.length})</h2>
          {active.length === 0 ? (
            <div className="flex flex-col items-center py-10 bg-[#1e293b]/40 rounded-2xl mb-5">
              <Crosshair size={44} color="#475569" />
              <p className="text-[#6b7280] mt-3">No matches selected yet</p>
              <button onClick={openAdd} className="text-[#8b5cf6] text-sm font-semibold mt-1">Add your first match</button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
              {active.map((s) => (
                <SelectionCard key={s.id} sel={s} onRemove={() => removeSelection(s.id)} />
              ))}
            </div>
          )}

          {done.length > 0 && (
            <>
              <h2 className="text-base font-bold text-[#e2e8f0] mb-2 mt-2">Completed ({done.length})</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {done.map((s) => (
                  <SelectionCard key={s.id} sel={s} onRemove={() => removeSelection(s.id)} dimmed />
                ))}
              </div>
            </>
          )}
        </>
      )}

      {showAdd && (
        <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-black/60 p-0 md:p-4" onClick={() => setShowAdd(false)}>
          <div className="bg-[#0f172a] border border-[#334155] rounded-t-2xl md:rounded-2xl w-full md:max-w-2xl max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="sh-add-modal">
            <div className="p-4 border-b border-[#334155] flex items-center justify-between">
              <h3 className="text-base font-bold text-white">Add a match (live & upcoming)</h3>
              <button onClick={loadPrematch} className="text-[#94a3b8] hover:text-white"><RefreshCw size={18} className={loadingPre ? "animate-spin" : ""} /></button>
            </div>
            <div className="p-3 border-b border-[#334155]">
              <div className="flex items-center gap-2 bg-[#1e293b] rounded-lg px-3 py-2">
                <Search size={16} className="text-[#64748b]" />
                <input data-testid="sh-search" value={search} onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search players…" className="bg-transparent text-sm text-white outline-none flex-1" />
              </div>
              <p className="text-[11px] text-[#64748b] mt-2">
                Each player shows the live set-handicap price. Cloudbet usually opens this market near
                start time — earlier matches show "opens at start"; the bot still places automatically the moment it's priced.
              </p>
            </div>
            <div className="overflow-y-auto p-3 space-y-2">
              {loadingPre ? (
                <div className="flex justify-center py-8"><Loader2 className="animate-spin text-[#8b5cf6]" /></div>
              ) : filteredPre.length === 0 ? (
                <p className="text-center text-[#6b7280] py-8 text-sm">No matches found right now.</p>
              ) : (
                filteredPre.map((m) => {
                  const added = selectedEventIds.has(m.event_id);
                  return (
                    <div key={m.event_id} className="bg-[#1e293b] rounded-xl p-3" data-testid="sh-prematch-row">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-[11px] text-[#64748b]">{m.competition} · {m.is_live ? (m.score || "Live") : fmtTime(m.start_time)}</span>
                        <span className="flex items-center gap-1.5">
                          {m.is_live && <span className="text-[10px] text-[#ef4444] font-bold flex items-center gap-1" data-testid="sh-live-badge"><span className="w-1.5 h-1.5 rounded-full bg-[#ef4444] animate-pulse" />LIVE</span>}
                          {added && <span className="text-[10px] text-[#10b981] font-bold">ADDED</span>}
                        </span>
                      </div>
                      <p className="text-sm text-white font-semibold mb-2">{m.name}</p>
                      <div className="flex gap-2">
                        {[["home", m.home_player, m.home_sh_odds], ["away", m.away_player, m.away_sh_odds]].map(([side, player, shOdds]) => (
                          <button key={side} disabled={added || busyEvent === m.event_id + side}
                            data-testid={`sh-pick-${side}`}
                            onClick={() => addSelection(m.event_id, side, player)}
                            className="flex-1 flex flex-col items-center bg-[#0f172a] hover:bg-[#8b5cf6]/20 border border-[#334155] hover:border-[#8b5cf6] disabled:opacity-40 disabled:hover:bg-[#0f172a] disabled:hover:border-[#334155] rounded-lg px-2 py-2 transition-colors">
                            <span className="text-[13px] text-[#e2e8f0] font-semibold truncate w-full text-center">{player}</span>
                            {busyEvent === m.event_id + side ? (
                              <span className="text-[11px] text-[#8b5cf6] font-bold mt-0.5">Adding…</span>
                            ) : (
                              <span className="text-[11px] font-bold mt-0.5" data-testid={`sh-odds-${side}`}>
                                <span className="text-[#64748b]">+{m.handicap || "1.5"} </span>
                                {shOdds
                                  ? <span className="text-[#8b5cf6]">@ {Number(shOdds).toFixed(2)}x</span>
                                  : <span className="text-[#475569] font-medium">· opens at start</span>}
                              </span>
                            )}
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
            <div className="p-3 border-t border-[#334155]">
              <button onClick={() => setShowAdd(false)} className="w-full bg-[#334155] hover:bg-[#475569] text-white font-semibold py-2.5 rounded-xl">Done</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const SelectionCard = ({ sel, onRemove, dimmed }) => (
  <div className={`bg-[#1e293b] rounded-2xl p-4 ${dimmed ? "opacity-70" : ""}`} style={{ borderLeft: `3px solid ${(STATUS[sel.status] || STATUS.waiting).color}` }} data-testid="sh-selection-card">
    <div className="flex items-start justify-between mb-2">
      <div className="flex-1 min-w-0">
        <p className="text-[11px] text-[#64748b]">{sel.event_name}</p>
        <p className="text-[15px] font-bold text-white truncate" data-testid="sh-backed-player">
          Backing: <span className="text-[#a78bfa]">{sel.our_player}</span>
        </p>
        <p className="text-[12px] text-[#94a3b8]">+{sel.handicap} sets · Best of {sel.best_of} · vs {sel.opponent}</p>
      </div>
      <button data-testid="sh-remove-btn" onClick={onRemove} className="text-[#64748b] hover:text-[#ef4444] p-1.5"><Trash2 size={16} /></button>
    </div>
    <div className="flex items-center justify-between">
      <StatusBadge status={sel.status} />
      <span className="text-[11px] text-[#475569]">
        {sel.bet_count > 0 ? `${sel.bet_count} bet${sel.bet_count > 1 ? "s" : ""} placed` : (sel.start_time ? fmtTime(sel.start_time) : "")}
      </span>
    </div>
    {sel.status_reason && <p className="text-[11px] text-[#64748b] mt-2">{sel.status_reason}</p>}
  </div>
);
